import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { bindAccountIdentity, sendAccountIdentityVerificationCode } from '../../../store/request';
import type { AccountIdentityType } from '../../../types/account';
import { normalizeIdentity, resolveAccount } from '../../../utils/identity';
import useCountdown from '../../../hooks/useCountDown';

type BindIdentityModalProps = {
  open: boolean;
  identityType: AccountIdentityType;
  onClose: () => void;
  onSuccess?: () => void;
};

function isValidIdentity(identityType: AccountIdentityType, raw: string) {
  const credential = resolveAccount(raw);
  if (!credential) return false;
  if (identityType === 'PHONE') return 'phone' in credential;
  return 'email' in credential;
}

export function BindIdentityModal({ open, identityType, onClose, onSuccess }: BindIdentityModalProps) {
  const { t } = useTranslation();
  const { countdown, isRunning, startCountdown, resetCountdown } = useCountdown();
  const [identity, setIdentity] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIdentity('');
    setCode('');
    setSubmitting(false);
    setSending(false);
    resetCountdown();
    // 仅在打开时重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, identityType]);

  const sendCode = async () => {
    if (isRunning || sending) return;
    const next = normalizeIdentity(identity);
    if (!isValidIdentity(identityType, next)) {
      message.warning(
        identityType === 'PHONE' ? t('userCenter.phone_invalid') : t('userCenter.change_email_invalid')
      );
      return;
    }
    setSending(true);
    try {
      const res = await sendAccountIdentityVerificationCode({
        identityType,
        identity: next
      });
      if (res.code === 200) {
        message.success(t('login.code_sent'));
        startCountdown();
      } else if (res.code !== -1) {
        message.error(res.message || t('login.send_failed'));
      }
    } catch {
      message.error(t('login.send_failed_retry'));
    } finally {
      setSending(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    const nextIdentity = normalizeIdentity(identity);
    const nextCode = code.trim();
    if (!isValidIdentity(identityType, nextIdentity)) {
      message.warning(
        identityType === 'PHONE' ? t('userCenter.phone_invalid') : t('userCenter.change_email_invalid')
      );
      return;
    }
    if (!/^[0-9]{6}$/.test(nextCode)) {
      message.warning(t('userCenter.unbind_code_invalid'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await bindAccountIdentity({
        identityType,
        identity: nextIdentity,
        code: nextCode
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('userCenter.bind_failed'));
        return;
      }
      message.success(res.message || t('userCenter.bind_success'));
      onClose();
      onSuccess?.();
    } catch {
      message.error(t('userCenter.bind_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={identityType === 'PHONE' ? t('userCenter.bind_phone_title') : t('userCenter.bind_email_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={440}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" className="wb-btn-primary rounded-lg" loading={submitting} onClick={submit}>
            {t('userCenter.bind_confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-1">
        <p className="mb-0 text-sm leading-relaxed text-slate-500">{t('userCenter.bind_hint')}</p>
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">
            {identityType === 'PHONE' ? t('userCenter.phone') : t('layout.menu_email')}
          </div>
          <Input
            value={identity}
            maxLength={320}
            placeholder={
              identityType === 'PHONE' ? t('userCenter.bind_phone_placeholder') : t('userCenter.bind_email_placeholder')
            }
            onChange={(event) => setIdentity(event.target.value)}
          />
        </div>
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('login.code_label')}</div>
          <div className="flex gap-2">
            <Input
              value={code}
              maxLength={6}
              inputMode="numeric"
              placeholder={t('login.code_placeholder')}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <Button className="shrink-0 rounded-lg" loading={sending} disabled={isRunning} onClick={sendCode}>
              {isRunning ? `${countdown}s` : t('login.send_code')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
